/**
 * shared/viTriChu.js: tìm câu chữ AI trích ở đâu trên ảnh slide, và xếp các
 * nhãn số ở lề phải để mũi tên không đè lên nhau.
 *
 * Đầu vào là các mẩu chữ functions/hinhSlide.js lấy từ PDF: { s, x, y, w, h },
 * toạ độ theo tỷ lệ 0..1 của trang. Hàm thuần, dùng ở trang founder và trang
 * quản trị.
 *
 * Cách dò: bỏ hết khoảng trắng và so chữ thường, vì PDF hay tách một câu thành
 * nhiều mẩu, thêm hoặc bớt dấu cách. Không thấy nguyên câu thì thử từng vế
 * (tách ở dấu phẩy, chấm), rồi tới phần đầu câu. Vẫn không thấy (chữ nằm trong
 * ảnh, hoặc AI chép sai) thì trả về rỗng: báo cáo vẫn hiện nhận xét, chỉ không
 * gạch chân.
 */

const NGOAC = { '«': '"', '»': '"', '“': '"', '”': '"', '„': '"', '‘': "'", '’': "'" };

function chuanKyTu(c) {
  return (NGOAC[c] || c).normalize('NFC').toLocaleLowerCase('vi');
}

/** Chuỗi so khớp của một câu: chữ thường, không khoảng trắng. */
export function chuanSoKhop(s) {
  return [...String(s || '').normalize('NFC')].filter((c) => !/\s/.test(c)).map(chuanKyTu).join('');
}

/** Ghép các mẩu chữ thành một chuỗi, nhớ mỗi ký tự thuộc mẩu nào. */
function dungChuoi(chu) {
  let s = '';
  const map = [];
  chu.forEach((m, i) => {
    const kt = [...String(m.s || '').normalize('NFC')];
    kt.forEach((c, k) => {
      if (/\s/.test(c)) return;
      const n = chuanKyTu(c);
      for (const x of n) { s += x; map.push({ i, k, n: kt.length }); }
    });
  });
  return { s, map };
}

function hopTheoMau(chu, map, tu, den) {
  const theoMau = new Map();
  for (let j = tu; j < den; j += 1) {
    const { i, k, n } = map[j];
    const o = theoMau.get(i) || { i, a: k, b: k, n };
    o.a = Math.min(o.a, k); o.b = Math.max(o.b, k);
    theoMau.set(i, o);
  }
  return [...theoMau.values()].map(({ i, a, b, n }) => {
    const m = chu[i];
    return { x: m.x + (m.w * a) / n, y: m.y, w: (m.w * (b + 1 - a)) / n, h: m.h };
  });
}

/** Gộp các khung trên cùng một dòng, liền nhau, thành một khung. */
function gopDong(khung) {
  const ds = [...khung].sort((p, q) => p.y - q.y || p.x - q.x);
  const ra = [];
  for (const k of ds) {
    const t = ra[ra.length - 1];
    const cungDong = t && Math.abs(t.y + t.h / 2 - (k.y + k.h / 2)) < Math.max(t.h, k.h) * 0.5;
    if (cungDong && k.x <= t.x + t.w + 0.03) {
      const x = Math.min(t.x, k.x);
      const y = Math.min(t.y, k.y);
      t.w = Math.max(t.x + t.w, k.x + k.w) - x;
      t.h = Math.max(t.y + t.h, k.y + k.h) - y;
      t.x = x; t.y = y;
    } else ra.push({ ...k });
  }
  return ra;
}

const TOI_THIEU = 6;

/**
 * Tìm câu trích trên trang. Trả về các khung { x, y, w, h } (có thể nhiều
 * khung khi câu xuống dòng), rỗng khi không tìm thấy.
 */
export function timCau(chu, cau) {
  if (!Array.isArray(chu) || !chu.length) return [];
  const q = chuanSoKhop(cau).replace(/^["'(]+|["'.,;:!?)]+$/g, '');
  if (q.length < 3) return [];
  const { s, map } = dungChuoi(chu);
  const thu = (x) => {
    const at = x.length >= Math.min(TOI_THIEU, q.length) ? s.indexOf(x) : -1;
    return at < 0 ? null : hopTheoMau(chu, map, at, at + x.length);
  };
  const nguyen = thu(q);
  if (nguyen) return gopDong(nguyen);

  // Từng vế của câu.
  const ve = String(cau).split(/[,.;:!?\n]+/).map(chuanSoKhop).filter((x) => x.length >= 8);
  const timDuoc = ve.map(thu).filter(Boolean).flat();
  if (timDuoc.length) return gopDong(timDuoc);

  // Phần đầu câu, ngắn dần.
  for (let n = Math.min(q.length - 1, 40); n >= 12; n -= 4) {
    const k = thu(q.slice(0, n));
    if (k) return gopDong(k);
  }
  return [];
}

/**
 * Xếp nhãn số ở lề phải: mỗi nhãn đặt ngang với khung của nó, nhưng không sát
 * nhau quá khoangCach (tính theo tỷ lệ chiều cao trang). Trả về y tâm nhãn.
 */
export function xepNhan(dsY, khoangCach = 0.09) {
  const thuTu = dsY.map((y, i) => ({ y, i })).sort((a, b) => a.y - b.y);
  const ra = new Array(dsY.length);
  let truoc = -Infinity;
  for (const { y, i } of thuTu) {
    const dat = Math.max(y, truoc + khoangCach, khoangCach / 2);
    ra[i] = dat; truoc = dat;
  }
  // Tràn đáy thì đẩy cả cụm lên.
  const tran = Math.max(0, truoc - (1 - khoangCach / 2));
  if (tran > 0) {
    let sau = Infinity;
    for (const { i } of [...thuTu].reverse()) {
      ra[i] = Math.min(ra[i] - tran, sau - khoangCach);
      sau = ra[i];
    }
  }
  return ra;
}

/**
 * Các điểm cần sửa của một slide, kèm khung tìm được trên ảnh. Bản nhận xét
 * cũ (chưa có slide.diem) thì lấy câu hienTai làm một điểm.
 */
export function diemTrenSlide(slide, chu) {
  const ds = Array.isArray(slide?.diem) && slide.diem.length
    ? slide.diem
    : (slide?.hienTai ? [{ trich: slide.hienTai, viSao: '', suaGi: '', cachSua: slide.vietLai || '' }] : []);
  return ds.map((d) => ({ ...d, khung: d.trich ? timCau(chu, d.trich) : [] }));
}
